import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from './cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  loading?: boolean;
  fullWidth?: boolean;
}

const VARIANT: Record<ButtonVariant, string> = {
  primary: cn(
    'bg-accent text-white',
    'hover:bg-accent-600',
    'active:bg-accent-600/90',
    'disabled:bg-accent/50',
  ),
  secondary: cn(
    'bg-surface-1 text-text-primary border border-border-default',
    'hover:bg-surface-2 hover:border-border-strong',
    'active:bg-surface-2/90',
    'disabled:bg-surface-1/50 disabled:text-text-tertiary',
  ),
  ghost: cn(
    'bg-transparent text-text-secondary',
    'hover:bg-surface-1 hover:text-text-primary',
    'active:bg-surface-2',
    'disabled:text-text-tertiary',
  ),
  danger: cn(
    'bg-state-danger text-white',
    'hover:bg-state-danger/85',
    'active:bg-state-danger/75',
    'disabled:bg-state-danger/40',
  ),
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-7  text-xs  px-2.5 gap-1.5 rounded-sm',
  md: 'h-9  text-sm  px-3.5 gap-2   rounded-md',
  lg: 'h-11 text-base px-5 gap-2.5 rounded-md',
};

const SPINNER_SIZE: Record<ButtonSize, string> = {
  sm: 'h-3 w-3',
  md: 'h-3.5 w-3.5',
  lg: 'h-4 w-4',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    leadingIcon,
    trailingIcon,
    loading = false,
    fullWidth = false,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(
        'relative inline-flex items-center justify-center font-medium cursor-pointer',
        'transition-colors duration-[var(--duration-fast)] ease-[var(--ease-standard)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        'focus-visible:ring-border-focus focus-visible:ring-offset-surface-base',
        'disabled:cursor-not-allowed',
        SIZE[size],
        VARIANT[variant],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Loader2 className={cn('animate-spin', SPINNER_SIZE[size])} />
        </span>
      )}
      <span className={cn('inline-flex items-center gap-[inherit]', loading && 'opacity-0')}>
        {leadingIcon && <span className="inline-flex shrink-0">{leadingIcon}</span>}
        {children}
        {trailingIcon && <span className="inline-flex shrink-0">{trailingIcon}</span>}
      </span>
    </button>
  );
});
